import { z } from "zod";

import { ArrayBinaryOperator, TernaryOperator } from "../../operators.ts";
import type { IResolvableObjectArrayValue, Obj, ResolvableBooleanValue } from "../../values.ts";
import { ObjZod, ResolvableBooleanValueUnion, ResolvableObjectArrayValue } from "../../values.ts";
import type { IConditionFor } from "../common.ts";

// Zod value schemas
const ObjectArrayValue = z.union([z.array(ObjZod), ResolvableObjectArrayValue]);

// Zod schemas with type discriminator
export const ObjectArrayBinaryCondition = z.object({
  type: z.literal("objectArray"),
  operator: ArrayBinaryOperator,
  subject: ObjectArrayValue,
  condition: ResolvableBooleanValueUnion,
});
export type ObjectArrayBinaryCondition = z.infer<typeof ObjectArrayBinaryCondition>;

export const ObjectArrayTernaryCondition = z.object({
  type: z.literal("objectArray"),
  operator: TernaryOperator,
  subject: ObjectArrayValue,
  leftCondition: ResolvableBooleanValueUnion,
  rightCondition: ResolvableBooleanValueUnion,
});
export type ObjectArrayTernaryCondition = z.infer<typeof ObjectArrayTernaryCondition>;

export const ObjectArrayCondition = z.union([ObjectArrayBinaryCondition, ObjectArrayTernaryCondition]);
export type ObjectArrayCondition = z.infer<typeof ObjectArrayCondition>;

// Interfaces for complex types (verified to match schemas)
export interface IObjectArrayBinaryCondition extends IConditionFor<ArrayBinaryOperator> {
  type: "objectArray";
  subject: Obj[] | IResolvableObjectArrayValue;
  condition: ResolvableBooleanValue;
}

export interface IObjectArrayTernaryCondition extends IConditionFor<TernaryOperator> {
  type: "objectArray";
  subject: Obj[] | IResolvableObjectArrayValue;
  leftCondition: ResolvableBooleanValue;
  rightCondition: ResolvableBooleanValue;
}

// Type compatibility assertions - compile-time check that inferred types match interfaces
type _AssertBinaryCompatible = ObjectArrayBinaryCondition extends IObjectArrayBinaryCondition ? true : never;
type _AssertTernaryCompatible = ObjectArrayTernaryCondition extends IObjectArrayTernaryCondition ? true : never;
const _binaryCheck: _AssertBinaryCompatible = true;
const _ternaryCheck: _AssertTernaryCompatible = true;
void _binaryCheck;
void _ternaryCheck;
